import { AdminPatientListResponseDto } from "@/data/dto/admin/admin-patient-list-response-dto";
import { DoctorListResponseDto } from "@/data/dto/admin/doctor-list-response-dto";
import { DoctorDetailInfoResponseDto } from "@/data/dto/admin/doctor-response-dto";
import { DoctorCreateRequestPayload } from "@/data/payload/admin/doctor-create-request-payload";
import { DoctorUpdateRequestPayload } from "@/data/payload/admin/doctor-update-reqeust-payload";
import { GetDoctorRequestPayload } from "@/data/payload/admin/get-doctor-request-payload";
import { URLList } from "@/data/url_list";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";
import { DoctorCreateResponseDto } from "../dto/admin/doctor-create-reponse-dto";

export class AdminSource {
  constructor(private readonly client: FetchApiClientMethod) {}

  //MARK:- Doctor Management
  /**
   * 의사 목록 조회
   * @returns {Promise<SATResult<DoctorListResponseDto[], SATError>>} - 성공 시 의사 목록, 실패 시 SATError
   */
  async getDoctorList(): Promise<SATResult<DoctorListResponseDto[], SATError>> {
    try {
      const response = await this.client.get(URLList.DOCTOR.LIST);
      console.log("[AdminSource] :: getDoctorList :: 의사 목록 조회 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: getDoctorList :: 의사 목록 조회 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: getDoctorList | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 의사 등록
   * @param createData - 등록할 의사 정보
   * @returns {Promise<SATResult<DoctorListResponseDto, SATError>>} - 성공 시 등록된 의사 정보, 실패 시 SATError
   */
  async createDoctor(
    createData: DoctorCreateRequestPayload
  ): Promise<SATResult<DoctorCreateResponseDto, SATError>> {
    try {
      const response = await this.client.post(URLList.DOCTOR.CREATE, createData);
      console.log("[AdminSource] :: createDoctor :: 의사 등록 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: createDoctor :: 의사 등록 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: createDoctor | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 의사 정보 조회
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<DoctorListResponseDto, SATError>>} - 성공 시 의사 정보, 실패 시 SATError
   */
  async getDoctor(
    payload: GetDoctorRequestPayload
  ): Promise<SATResult<DoctorDetailInfoResponseDto, SATError>> {
    try {
      const response = await this.client.get(
        `${URLList.DOCTOR.GET}/${payload.doctorUuid}`
      );
      console.log("[AdminSource] :: getDoctor :: 의사 정보 조회 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: getDoctor :: 의사 정보 조회 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: getDoctor | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 의사 정보 업데이트
   * @param doctorUuid - 의사 UUID
   * @param updateData - 업데이트할 의사 정보
   * @returns {Promise<SATResult<DoctorUpdateResponseDto, SATError>>} - 성공 시 업데이트된 의사 정보, 실패 시 SATError
   */
  async updateDoctor(
    doctorUuid: string,
    updateData: DoctorUpdateRequestPayload
  ): Promise<SATResult<void, SATError>> {
    try {
      const response = await this.client.put(
        `${URLList.DOCTOR.UPDATE}/${doctorUuid}`,
        updateData
      );
      console.log("[AdminSource] :: updateDoctor :: 의사 정보 업데이트 성공", response.data);
      return SATResult.success(void 0);
    } catch (error: unknown) {
      console.log("[AdminSource] :: updateDoctor :: 의사 정보 업데이트 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: updateDoctor | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 의사 계정 활성화
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<void, SATError>>} - 성공 시 활성화 결과, 실패 시 SATError
   */
  async activateDoctor(doctorUuid: string): Promise<SATResult<void, SATError>> {
    try {
      const response = await this.client.put(`${URLList.DOCTOR.ACTIVATE}/${doctorUuid}`);
      console.log("[AdminSource] :: activateDoctor :: 의사 계정 활성화 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: activateDoctor :: 의사 계정 활성화 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: activateDoctor | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 의사 계정 비활성화
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<void, SATError>>} - 성공 시 비활성화 결과, 실패 시 SATError
   */
  async deactivateDoctor(doctorUuid: string): Promise<SATResult<void, SATError>> {
    try {
      const response = await this.client.put(
        `${URLList.DOCTOR.DEACTIVATE}/${doctorUuid}`
      );
      console.log("[AdminSource] :: deactivateDoctor :: 의사 계정 비활성화 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: deactivateDoctor :: 의사 계정 비활성화 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: deactivateDoctor | ", satError);
      return SATResult.failure(satError);
    }
  }

  //MARK:- Patient Management
  /**
   * 환자 목록 조회
   * @returns {Promise<SATResult<PatientListResponseDto[], SATError>>} - 성공 시 환자 목록, 실패 시 SATError
   */
  async getPatientList(): Promise<SATResult<AdminPatientListResponseDto[], SATError>> {
    try {
      const response = await this.client.get(URLList.PATIENT.LIST);
      console.log("[AdminSource] :: getPatientList :: 환자 목록 조회 성공", response.data);
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log("[AdminSource] :: getPatientList :: 환자 목록 조회 실패", error);
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: getPatientList | ", satError);
      return SATResult.failure(satError);
    }
  }

  /**
   * 비밀번호 설정 메시지 재전송
   * @param doctorUuid - 의사 UUID
   * @returns {Promise<SATResult<void, SATError>>} - 성공 시 메시지 재전송 결과, 실패 시 SATError
   */
  async sendPasswordChangeEmail(doctorUuid: string): Promise<SATResult<void, SATError>> {
    try {
      const response = await this.client.post(
        `${URLList.DOCTOR.SEND_PASSWORD_CHANGE_EMAIL}/${doctorUuid}`
      );
      console.log(
        "[AdminSource] :: sendPasswordChangeEmail :: 비밀번호 설정 메시지 재전송 성공",
        response.data
      );
      return SATResult.success(response.data);
    } catch (error: unknown) {
      console.log(
        "[AdminSource] :: sendPasswordChangeEmail :: 비밀번호 설정 메시지 재전송 실패",
        error
      );
      const satError = errorConverter.convertError(error);
      console.log("[AdminSource] :: sendPasswordChangeEmail | ", satError);
      return SATResult.failure(satError);
    }
  }
}
